class MiniCollectionListSwiper {
  constructor(container) {
    this.container = container;
    if (!this.container) {
      console.error('MiniCollectionListSwiper: Invalid container element provided.');
      return;
    }
    this.swiperContainer = this.container.querySelector('.swiper-container');
    this.swiper = null; // Initialize swiper instance variable
    this.initAttempts = 0;
    this.maxInitAttempts = 10;
    this.initTimeout = 200; // ms to wait between attempts

    this.container.swiperInstance = this;
    this.attemptInitialization();
  }

  attemptInitialization() {
    if (typeof Swiper === 'undefined') {
      this.initAttempts++;
      if (this.initAttempts <= this.maxInitAttempts) {
        setTimeout(() => this.attemptInitialization(), this.initTimeout);
      } else {
        console.error(`Swiper library not found for ${this.container.id} after ${this.maxInitAttempts} attempts.`);
      }
      return;
    }

    if (!this.swiperContainer) {
       console.error(`Swiper container (.swiper-container) not found within section ${this.container.id}. Cannot initialize Swiper.`);
       return;
    }

    // If checks pass, initialize
    this.initSwiper();
  }

  initSwiper() {
    // Ensure we don't re-initialize if already done
    if (this.swiper) {
      return;
    }
    
    const desktopItems = parseInt(this.swiperContainer.dataset.desktopItems, 10) || 4;
    const mobileItems = parseInt(this.swiperContainer.dataset.mobileItems, 10) || 3;
    // Use smaller space for desktop for a more compact look
    const desktopSpaceBetween = 8; // Further reduced space 
    const mobileSpaceBetween = 16; // Keep mobile space as is
    const currentSpaceBetween = window.innerWidth >= 750 ? desktopSpaceBetween : mobileSpaceBetween;

    try {
      this.swiper = new Swiper(this.swiperContainer, {
        slidesPerView: mobileItems,
        spaceBetween: currentSpaceBetween,
        watchOverflow: true,
        preventInteractionOnTransition: true,
        speed: 400,
        freeMode: true,
        freeModeMomentum: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: true, 
        lazy: {
           loadPrevNext: true,
           loadOnTransitionStart: true,
           loadPrevNextAmount: 2, // Load 2 slides ahead/behind
           checkInView: true, // Force checking if slides are in view
        },
        
        breakpoints: {
          750: {
            slidesPerView: desktopItems,
            spaceBetween: desktopSpaceBetween
          }
        },

        navigation: {
          nextEl: this.container.querySelector('.swiper-button-next'),
          prevEl: this.container.querySelector('.swiper-button-prev'),
        },

        on: {
          init: (swiper) => {
            this.updateSlideWidths(swiper); // Calculate initial widths
            this.updateVisibility(swiper);
            this.forceLoadVisibleImages(swiper);
          },
          resize: (swiper) => {
            swiper.params.spaceBetween = window.innerWidth >= 750 ? desktopSpaceBetween : mobileSpaceBetween;
            this.updateSlideWidths(swiper); // Recalculate widths on resize
            this.updateVisibility(swiper);
            swiper.update(); // Update swiper layout
            this.forceLoadVisibleImages(swiper);
          },
          slideChange: (swiper) => {
             if (swiper.lazy) swiper.lazy.load();
          },
          afterInit: (swiper) => {
            // Extra check to ensure images load after full initialization
            setTimeout(() => this.forceLoadVisibleImages(swiper), 100);
          }
        },
      });
    } catch (error) {
       console.error(`Error initializing Swiper for ${this.container.id}:`, error);
       this.swiper = null; // Ensure swiper is null if init fails
    }
  }

  forceLoadVisibleImages(swiper) {
    if (!swiper || !swiper.slides) return;
    
    // Fallback for lazy loading - force load visible slides
    const visibleSlides = Array.from(swiper.slides).slice(
      Math.max(0, swiper.activeIndex - 1),
      Math.min(swiper.slides.length, swiper.activeIndex + (swiper.params.slidesPerView * 2) + 1)
    );
    
    visibleSlides.forEach(slide => {
      const lazyElements = slide.querySelectorAll('.swiper-lazy');
      lazyElements.forEach(element => {
        if (element.classList.contains('swiper-lazy-loaded')) return;
        
        if (element.tagName === 'IMG') {
          const src = element.dataset.src;
          const srcset = element.dataset.srcset;
          
          if (src) element.src = src;
          if (srcset) element.srcset = srcset;
          element.classList.add('swiper-lazy-loaded');
          
          // Find and hide preloader
          const preloader = element.nextElementSibling;
          if (preloader && preloader.classList.contains('swiper-lazy-preloader')) {
            preloader.style.display = 'none';
          }
        }
      });
    });
    
    // Manually initiate Swiper's lazy loading
    if (swiper.lazy && typeof swiper.lazy.load === 'function') {
      swiper.lazy.load();
    }
  }

  updateSlideWidths(swiper) {
    if (!swiper || swiper.destroyed || !swiper.el) return;

    const containerWidth = swiper.el.clientWidth;
    const isMobile = window.innerWidth < 750;
    const itemsPerView = isMobile ? 
      parseInt(this.swiperContainer.dataset.mobileItems, 10) || 3 :
      parseInt(this.swiperContainer.dataset.desktopItems, 10) || 4;
    const spaceBetween = swiper.params.spaceBetween;

    if (containerWidth <= 0 || itemsPerView <= 0) {
       return; 
    }

    // Calculate the width for each slide, accounting for space between
    const totalSpace = spaceBetween * (itemsPerView - 1);
    const availableWidth = containerWidth - totalSpace;
    const slideWidth = Math.floor(availableWidth / itemsPerView);

    swiper.slides.forEach(slide => {
       if(slide) slide.style.width = `${slideWidth}px`;
    });
  }

  updateVisibility(swiper) {
    if (!swiper || swiper.destroyed) return;
    
    const isMobile = window.innerWidth < 750;
    const slidesPerViewSetting = isMobile ? 
      parseInt(this.swiperContainer.dataset.mobileItems, 10) || 3 : 
      parseInt(this.swiperContainer.dataset.desktopItems, 10) || 4;

    const totalSlides = swiper.slides ? swiper.slides.length : 0;
    const wrapper = swiper.wrapperEl;
    const navigation = this.container.querySelectorAll('.swiper-button-prev, .swiper-button-next');

    // Show/hide navigation
    const showNavigation = totalSlides > slidesPerViewSetting;
    navigation.forEach(nav => {
      nav.style.display = showNavigation ? '' : 'none';
    });

    // Center slides and enable/disable interaction
    if (!showNavigation) {
       if (wrapper) wrapper.classList.add('is-centered');
       swiper.disable(); 
    } else {
       if (wrapper) wrapper.classList.remove('is-centered');
       swiper.enable();
    }
    
    // Swiper's watchOverflow should handle disabling nav buttons, 
    // but we explicitly hide them above if not needed at all.
    swiper.update(); // Update Swiper state
  }

  destroy() {
    if (this.swiper && this.swiper.destroy) {
      try {
        this.swiper.destroy(true, true);
      } catch (error) {
         console.error(`Error destroying Swiper for ${this.container.id}:`, error);
      } finally {
         this.swiper = null;
      }
    }
    if (this.container) {
      delete this.container.swiperInstance;
    }
  }
}

function debounce(func, wait, immediate) {
    var timeout;
    return function() {
        var context = this, args = arguments;
        var later = function() {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
};


// --- Initialization Logic --- //

const selectors = {
  section: '.mini-collection-list-section'
};

const swiperInstances = new Map();

function initializeSwiperForSection(sectionElement) {
  if (!sectionElement) return;
  const sectionId = sectionElement.id;
  
  // Destroy existing instance if present
  if (swiperInstances.has(sectionId)) {
     const existingInstance = swiperInstances.get(sectionId);
     if (existingInstance && typeof existingInstance.destroy === 'function') {
        existingInstance.destroy();
     }
     swiperInstances.delete(sectionId);
  }
  
  const newInstance = new MiniCollectionListSwiper(sectionElement);
  swiperInstances.set(sectionId, newInstance);
}

function initializeAllMiniCollectionSwipers() {
  document.querySelectorAll(selectors.section).forEach(section => {
     initializeSwiperForSection(section);
  });
}

const debouncedInitializeAllSwipers = debounce(initializeAllMiniCollectionSwipers, 250);

// Initial load
document.addEventListener('DOMContentLoaded', () => {
   initializeAllMiniCollectionSwipers();
});

// Handle Shopify theme editor updates
document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.matches(selectors.section)) {
     setTimeout(() => initializeSwiperForSection(event.target), 100); 
  }
});

document.addEventListener('shopify:section:unload', (event) => {
   if (event.target && event.target.matches(selectors.section)) {
      const sectionId = event.target.id;
      if (swiperInstances.has(sectionId)) {
         const instance = swiperInstances.get(sectionId);
         if (instance && typeof instance.destroy === 'function') {
            instance.destroy();
         }
         swiperInstances.delete(sectionId);
      }
   }
});

// Use debounced initializer for frequent events that might affect layout
window.addEventListener('resize', debouncedInitializeAllSwipers);
document.addEventListener('shopify:section:reorder', debouncedInitializeAllSwipers);
document.addEventListener('shopify:block:select', debouncedInitializeAllSwipers);
document.addEventListener('shopify:block:deselect', debouncedInitializeAllSwipers); 